import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import MasterFilter, { type DateRangeValue, useMasterFilter } from 'components/common/MasterFilter';
import type { FilterFieldConfig } from 'components/common/MasterFilter/types';
import PaginationFooter from 'components/common/PaginationFooter';
import moment from 'moment';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import 'moment/locale/es';

import { AddIcon, RefreshIcon } from 'assets/icons';
import DebtStatusBadge from 'components/UI/DebtStatusBadge';
import { useBusinessUnits } from 'contexts/BusinessUnitsContext';
import { useSelectedBusinessUnit } from 'contexts/SidebarContext';
import useCurrentUser from 'hooks/useCurrentUser';
import { useGetUniqueValuesOfLeadQuery } from 'services/api/leads.api';
import { useGenerateReportMutation, useGetReportsQuery } from 'services/api/reports.api';
import type { Report, ReportPeriodType, ReportStatus } from 'services/types/report';
import {
  MOBILE_PAGE_PT,
  NAVY,
  RADIUS_CARD,
  RADIUS_COIN,
  RADIUS_ITEM,
  SHADOW,
  SURFACE,
  SURFACE_CONTAINER_LOWEST,
  YELLOW,
} from 'styles/tokens';

const getStatusBadge = (status: ReportStatus) => {
  const statusConfig: Record<ReportStatus, { text: string; color: 'success' | 'warning' | 'info' | 'error' }> = {
    COMPLETED: { text: 'Completado', color: 'success' },
    PENDING: { text: 'Pendiente', color: 'warning' },
    GENERATING: { text: 'Generando', color: 'info' },
    FAILED: { text: 'Error', color: 'error' },
  };
  const config = statusConfig[status];
  return <DebtStatusBadge text={config.text} color={config.color} />;
};

const getPeriodTypeBadge = (periodType: ReportPeriodType) => {
  const periodConfig: Record<ReportPeriodType, { text: string; color: 'primary' | 'info' | 'success' }> = {
    daily: { text: 'Diario', color: 'success' },
    weekly: { text: 'Semanal', color: 'primary' },
    monthly: { text: 'Mensual', color: 'info' },
  };

  const config = periodConfig[periodType];
  return <DebtStatusBadge text={config.text} color={config.color} fontSize="10px" minWidth="60px" />;
};

const parseQueryParam = <T,>(param: string | null, defaultValue: T): T => {
  if (!param) return defaultValue;
  try {
    return JSON.parse(param) as T;
  } catch {
    return defaultValue;
  }
};

const DEFAULT_PAGE_SIZE = 15;

const REPORTS_FILTER_CONFIG: FilterFieldConfig[] = [
  { key: 'dateRange', kind: 'dateRange', label: 'Fecha', placement: 'bar' },
  {
    key: 'periodType',
    kind: 'singleSelect',
    label: 'Tipo',
    placement: 'bar',
    options: [
      { value: 'daily', label: 'Diario' },
      { value: 'weekly', label: 'Semanal' },
      { value: 'monthly', label: 'Mensual' },
    ],
  },
];

const Reports: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const isAdmin = currentUser?.isAdmin ?? false;
  const { businessUnits } = useBusinessUnits();
  const { selectedBusinessUnitId } = useSelectedBusinessUnit();
  const businessUnitId = selectedBusinessUnitId ?? -1;

  const queryPaginationModel = searchParams.get('paginationModel');
  const parsedPaginationModel = parseQueryParam<{ page: number; pageSize: number }>(queryPaginationModel, {
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const [paginationModel, setPaginationModel] = useState<{ page: number; pageSize: number }>(
    parsedPaginationModel.pageSize <= 100 && parsedPaginationModel.page >= 0
      ? parsedPaginationModel
      : { page: 0, pageSize: DEFAULT_PAGE_SIZE }
  );

  const {
    data: reportsData,
    isLoading,
    refetch,
    isFetching,
  } = useGetReportsQuery({
    paginationModel,
    businessUnitId: businessUnitId === -1 ? undefined : businessUnitId,
  });
  const reports = reportsData?.reports ?? [];
  const totalReports = reportsData?.total ?? 0;

  const [generateReport, { isLoading: isGenerating }] = useGenerateReportMutation();

  const [openDialog, setOpenDialog] = useState(false);
  const dialogBusinessUnitId = businessUnitId !== -1 ? businessUnitId : null;
  const [selectedDate, setSelectedDate] = useState<string>(moment().subtract(1, 'day').format('YYYY-MM-DD'));
  const [dialogPeriodType, setDialogPeriodType] = useState<ReportPeriodType>('daily');
  const [selectedSource, setSelectedSource] = useState<string>('');

  const { data: dialogSources } = useGetUniqueValuesOfLeadQuery(
    { businessUnitId: dialogBusinessUnitId ?? 0, startDate: null, endDate: null, attribute: 'source' },
    { skip: !openDialog || !dialogBusinessUnitId }
  );

  const [lookerUrl, setLookerUrl] = useState<string | null>(null);
  const [lookerBuName, setLookerBuName] = useState<string>('');
  const [iframeLoading, setIframeLoading] = useState(true);

  const businessUnitsWithLooker = useMemo(() => {
    if (!businessUnits) return [];
    return businessUnits.filter((bu) => bu.lookerReportUrl);
  }, [businessUnits]);

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const isDefault = paginationModel.page === 0 && paginationModel.pageSize === DEFAULT_PAGE_SIZE;
        if (isDefault) {
          prev.delete('paginationModel');
          return prev;
        }
        prev.set('paginationModel', JSON.stringify(paginationModel));
        return prev;
      },
      { replace: true }
    );
  }, [paginationModel, setSearchParams]);

  const dateFilter = useMasterFilter(REPORTS_FILTER_CONFIG, dialogBusinessUnitId, undefined, undefined, {
    urlNamespace: 'reports',
  });
  const dateRange = dateFilter.values.dateRange as DateRangeValue | undefined;
  const startDate = dateRange?.start ?? null;
  const endDate = dateRange?.end ?? null;
  const selectedPeriodType = (dateFilter.values.periodType as string | null) ?? null;

  const filteredReports = useMemo(() => {
    return reports.filter((report) => {
      const begin = moment(report.beginDate);
      if (startDate && begin.isBefore(startDate, 'day')) return false;
      if (endDate && begin.isAfter(endDate, 'day')) return false;
      if (selectedPeriodType && report.periodType !== selectedPeriodType) return false;
      return true;
    });
  }, [reports, startDate, endDate, selectedPeriodType]);

  const filteredLookerBUs = useMemo(() => {
    if (paginationModel.page > 0) return [];
    if (businessUnitId === -1) return businessUnitsWithLooker;
    return businessUnitsWithLooker.filter((bu) => bu.id === businessUnitId);
  }, [businessUnitsWithLooker, businessUnitId, paginationModel.page]);

  useEffect(() => {
    if (!isLoading && filteredLookerBUs.length === 1 && totalReports === 0) {
      const bu = filteredLookerBUs[0];
      if (bu.lookerReportUrl) {
        setLookerUrl(bu.lookerReportUrl);
        setLookerBuName(bu.name);
        setIframeLoading(true);
      }
    }
  }, [isLoading, filteredLookerBUs, totalReports]);

  const getBusinessUnitName = (buId: number) => {
    const bu = businessUnits?.find((b) => b.id === buId);
    return bu?.name || `BU #${buId}`;
  };

  const handleGenerateReport = async () => {
    if (!dialogBusinessUnitId) return;

    try {
      const result = await generateReport({
        businessUnitId: dialogBusinessUnitId,
        date: selectedDate,
        periodType: dialogPeriodType,
        ...(selectedSource ? { source: selectedSource } : {}),
      }).unwrap();
      setOpenDialog(false);
      navigate(`/reports/${result.id}`);
    } catch (error) {
      console.error('Error generating report:', error);
    }
  };

  const handleRowClick = (report: Report) => {
    navigate(`/reports/${report.id}`);
  };

  const handleOpenLooker = (url: string, buName: string) => {
    setLookerUrl(url);
    setLookerBuName(buName);
    setIframeLoading(true);
  };

  const handleCloseLooker = () => {
    setLookerUrl(null);
    setLookerBuName('');
  };

  // Looker iframe view
  if (lookerUrl) {
    return (
      <Box sx={{ p: 3, height: '100%', bgcolor: SURFACE }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 3,
          }}
        >
          <IconButton
            onClick={handleCloseLooker}
            size="small"
            sx={{
              color: NAVY,
              '&:hover': { bgcolor: alpha(NAVY, 0.06) },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 600, color: NAVY, fontFamily: 'Jost, sans-serif' }}>
            {lookerBuName}
          </Typography>
          <IconButton
            size="small"
            onClick={() => window.open(lookerUrl, '_blank', 'noopener,noreferrer')}
            title="Abrir en nueva pestaña"
            sx={{
              color: alpha(NAVY, 0.55),
              '&:hover': { bgcolor: alpha(NAVY, 0.06) },
            }}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Box>
        {iframeLoading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 120,
              bgcolor: SURFACE_CONTAINER_LOWEST,
              borderRadius: RADIUS_CARD,
            }}
          >
            <CircularProgress size={40} sx={{ color: alpha(NAVY, 0.3) }} />
          </Box>
        )}
        <Box
          component="iframe"
          sx={{
            width: '100%',
            height: 'calc(100vh - 150px)',
            border: 0,
            borderRadius: RADIUS_CARD,
            display: iframeLoading ? 'none' : 'block',
            boxShadow: SHADOW,
          }}
          src={lookerUrl}
          allowFullScreen
          sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
          onLoad={() => setIframeLoading(false)}
        />
      </Box>
    );
  }

  const headerCellSx = {
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontSize: '10px',
    color: alpha(NAVY, 0.5),
    borderBottom: `1px solid ${alpha(NAVY, 0.06)}`,
    py: 1.5,
    position: 'sticky',
    top: 0,
    bgcolor: SURFACE_CONTAINER_LOWEST,
    zIndex: 1,
  } as const;

  const rowSx = {
    cursor: 'pointer',
    transition: 'background-color 0.15s ease-in-out',
    '&:hover': { bgcolor: alpha(NAVY, 0.03) },
    '& td': { borderBottom: `1px solid ${alpha(NAVY, 0.06)}`, py: 1.5 },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ px: { xs: 2, lg: 3 }, pt: { xs: `${MOBILE_PAGE_PT}px`, md: 2, lg: 3 }, flexShrink: 0 }}>
        <MasterFilter {...dateFilter} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          m: { xs: 2, lg: 3 },
          mt: 1,
          bgcolor: SURFACE_CONTAINER_LOWEST,
          borderRadius: RADIUS_CARD,
          boxShadow: SHADOW,
        }}
      >
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 120,
            }}
          >
            <CircularProgress size={40} sx={{ color: alpha(NAVY, 0.3) }} />
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isAdmin && (
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => setOpenDialog(true)}
                          sx={{
                            height: 28,
                            borderRadius: RADIUS_COIN,
                            backgroundColor: YELLOW,
                            color: NAVY,
                            textTransform: 'none',
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 700,
                            fontSize: '0.75rem',
                            px: 1.5,
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
                            '&:hover': { backgroundColor: `${YELLOW}cc` },
                          }}
                        >
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                            Generar Reporte
                          </Box>
                          <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                            Nuevo
                          </Box>
                        </Button>
                      )}
                      <IconButton
                        onClick={() => {
                          refetch().catch(console.error);
                        }}
                        disabled={isFetching}
                        size="small"
                        sx={{
                          color: alpha(NAVY, 0.55),
                          transition: 'all 0.15s ease-in-out',
                          '&:hover': { bgcolor: alpha(NAVY, 0.06) },
                        }}
                      >
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell sx={headerCellSx}>Tipo</TableCell>
                  <TableCell sx={headerCellSx}>Fecha</TableCell>
                  <TableCell sx={{ ...headerCellSx, display: { xs: 'none', sm: 'table-cell' } }}>Generado</TableCell>
                  <TableCell align="center" sx={headerCellSx}>
                    Estado
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Looker Reports */}
                {filteredLookerBUs.map((bu) => (
                  <TableRow
                    key={`looker-${bu.id}`}
                    onClick={() => bu.lookerReportUrl && handleOpenLooker(bu.lookerReportUrl, bu.name)}
                    sx={rowSx}
                  >
                    <TableCell>
                      <Typography sx={{ color: NAVY, fontWeight: 500 }}>{bu.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <DebtStatusBadge text="Looker" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5) }}>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.5) }}>
                        -
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <DebtStatusBadge text="Disponible" color="success" />
                    </TableCell>
                  </TableRow>
                ))}
                {/* Automatic Reports */}
                {filteredReports.map((report) => (
                  <TableRow key={report.id} onClick={() => handleRowClick(report)} sx={rowSx}>
                    <TableCell>
                      <Typography sx={{ color: NAVY, fontWeight: 500 }}>{report.title}</Typography>
                    </TableCell>
                    <TableCell>{getPeriodTypeBadge(report.periodType)}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6) }}>
                        {moment(report.endDate).format('DD MMM YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6) }}>
                        {moment(report.createdAt).format('DD MMM YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{getStatusBadge(report.status)}</TableCell>
                  </TableRow>
                ))}
                {filteredLookerBUs.length === 0 && filteredReports.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 6, borderBottom: 'none' }}>
                      <Typography sx={{ color: alpha(NAVY, 0.4) }}>No hay reportes disponibles</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <PaginationFooter
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          rowCount={totalReports}
          onPageChange={(page) => setPaginationModel((prev) => ({ ...prev, page }))}
          onPageSizeChange={(pageSize) => setPaginationModel((prev) => ({ ...prev, pageSize, page: 0 }))}
          pageSizeOptions={[15, 25, 100]}
          sx={{ mt: 0, borderRadius: 0, borderTop: `1px solid ${alpha(NAVY, 0.06)}` }}
        />
      </Box>

      {/* Generate Report Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: NAVY, fontWeight: 600, fontFamily: 'Jost, sans-serif' }}>
          Generar Nuevo Reporte
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogBusinessUnitId ? (
              <Typography variant="body2" sx={{ color: alpha(NAVY, 0.6) }}>
                Unidad de negocio: <strong style={{ color: NAVY }}>{getBusinessUnitName(dialogBusinessUnitId)}</strong>
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                Selecciona una unidad de negocio en la barra lateral para generar el reporte.
              </Typography>
            )}
            <TextField
              select
              label="Tipo de Reporte"
              value={dialogPeriodType}
              onChange={(e) => setDialogPeriodType(e.target.value as ReportPeriodType)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  '& fieldset': { borderColor: alpha(NAVY, 0.12) },
                  '&:hover fieldset': { borderColor: alpha(NAVY, 0.25) },
                },
              }}
            >
              <MenuItem value="daily">Diario</MenuItem>
              <MenuItem value="weekly">Semanal</MenuItem>
              <MenuItem value="monthly">Mensual</MenuItem>
            </TextField>
            <TextField
              select
              label="Origen"
              value={selectedSource}
              onChange={(e) => setSelectedSource(e.target.value)}
              fullWidth
              helperText="Deja 'Todos los orígenes' para un reporte global de la unidad de negocio"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  '& fieldset': { borderColor: alpha(NAVY, 0.12) },
                  '&:hover fieldset': { borderColor: alpha(NAVY, 0.25) },
                },
              }}
            >
              <MenuItem value="">Todos los orígenes</MenuItem>
              {(dialogSources ?? []).filter(Boolean).map((src) => (
                <MenuItem key={src} value={src}>
                  {src}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={
                dialogPeriodType === 'weekly'
                  ? 'Fecha de fin de semana'
                  : dialogPeriodType === 'monthly'
                    ? 'Mes del Reporte'
                    : 'Fecha del Reporte'
              }
              type={dialogPeriodType === 'monthly' ? 'month' : 'date'}
              value={dialogPeriodType === 'monthly' ? selectedDate.slice(0, 7) : selectedDate}
              onChange={(e) =>
                setSelectedDate(dialogPeriodType === 'monthly' ? `${e.target.value}-01` : e.target.value)
              }
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              helperText={
                dialogPeriodType === 'weekly'
                  ? 'Selecciona el último día de la semana (7 días hacia atrás)'
                  : dialogPeriodType === 'monthly'
                    ? 'Selecciona el mes a reportar'
                    : 'Selecciona la fecha para la cual generar el reporte (normalmente el día anterior)'
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: RADIUS_ITEM,
                  '& fieldset': {
                    borderColor: alpha(NAVY, 0.12),
                  },
                  '&:hover fieldset': {
                    borderColor: alpha(NAVY, 0.25),
                  },
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              color: alpha(NAVY, 0.6),
              borderRadius: RADIUS_COIN,
              '&:hover': { bgcolor: alpha(NAVY, 0.06) },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleGenerateReport().catch(console.error);
            }}
            disabled={!dialogBusinessUnitId || isGenerating}
            startIcon={isGenerating ? <CircularProgress size={16} /> : null}
            sx={{
              height: 36,
              borderRadius: RADIUS_COIN,
              backgroundColor: YELLOW,
              color: NAVY,
              textTransform: 'none',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: '0.8rem',
              px: 2,
              '&:hover': { backgroundColor: `${YELLOW}cc` },
              '&.Mui-disabled': { backgroundColor: `${NAVY}14`, color: `${NAVY}60` },
            }}
          >
            {isGenerating ? 'Generando...' : 'Generar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reports;
